import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { discoverListQuery } from '@xdocs/schemas';
import type { DiscoverListResult, DiscoverListRow, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

const PAGE_SIZE = 12;

export interface UseDiscoverListResult {
  rows: readonly DiscoverListRow[];
  total: number | null;
  hasMore: boolean;
  categoryId: string | null;
  setCategoryId: (categoryId: string | null) => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  onRetry: () => void;
  onLoadMore: () => void;
}

/**
 * `discover.list` (F62) — the `/discover` public gallery: approved public
 * shares only, F31 category filter, mobile-rule "Load more" append (0.7) —
 * this is a card grid, not a `<DataTable>`, so it mirrors `useSearchQuery`'s
 * hand-rolled append pattern rather than the DataTable-driven one.
 *
 * Deliberately WORKS while logged out (F62/F63 AC: public reads need no
 * account) — `enabled` gates only on a live socket, never on `userId`.
 */
export function useDiscoverList(): UseDiscoverListResult {
  const { wsClient, connectionState } = useWs();
  const [categoryId, setCategoryIdState] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<DiscoverListRow[]>([]);
  const appendModeRef = useRef(false);

  const queryInput = {
    page,
    pageSize: PAGE_SIZE,
    sort: 'publishedAt' as const,
    dir: 'desc' as const,
    ...(categoryId ? { categoryId } : {}),
  };

  const enabled = connectionState === 'connected';

  const result = useQuery<DiscoverListResult, ErrorAlert>({
    queryKey: ['discover', 'list', queryInput],
    queryFn: () => requestTyped(wsClient, 'discover.list', discoverListQuery.parse(queryInput)),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  const setCategoryId = useCallback((next: string | null) => {
    appendModeRef.current = false;
    setPage(1);
    setCategoryIdState(next);
  }, []);

  const onLoadMore = useCallback(() => {
    if (result.isFetching) return;
    appendModeRef.current = true;
    setPage((p) => p + 1);
  }, [result.isFetching]);

  const onRetry = useCallback(() => void result.refetch(), [result]);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    hasMore,
    categoryId,
    setCategoryId,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    onRetry,
    onLoadMore,
  };
}
